import React, {useState, useEffect} from 'react';
import {makeStyles} from 'tss-react/mui';
import {FormattedMessage, useIntl} from 'react-intl';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Alert from '@mui/material/Alert';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ProgressStepper from '../components/ProgressStepper';
import IdCardHelpIllustration from '../components/IdCardHelpIllustration';
import {useDocumentCollection} from '../context/DocumentCollectionContext';
import {SCREENS} from '../constants/documentCollection';
import {submitHarHabituchDetails} from '../api/documentCollectionApi';
import {validateDate, validateDateNotInFuture, validateIdNumber, formatDateInput} from '../utils/harHabituchValidation';

const useStyles = makeStyles()((theme) => ({
  container: {
    maxWidth: 500,
    margin: '0 auto',
    direction: 'ltr',
    textAlign: 'left',
    padding: '0 16px 40px',
    '@media (min-width: 768px)': {
      padding: '0 24px 40px',
    },
  },
  helpLink: {
    display: 'block',
    textAlign: 'center',
    color: theme.palette.secondary?.main || theme.palette.primary.main,
    fontSize: theme.typography.pxToRem(15),
    fontWeight: 500,
    textDecoration: 'underline',
    cursor: 'pointer',
    marginBottom: 20,
    background: 'none',
    border: 'none',
    padding: 0,
    width: '100%',
  },
  disclaimerText: {
    fontSize: theme.typography.pxToRem(13),
    lineHeight: 1.6,
    textAlign: 'right',
    direction: 'rtl',
  },
  outlinedField: {
    '& .MuiInputLabel-root': {
      fontSize: theme.typography.pxToRem(15),
    },
  },
}));

const outlinedFieldSx = {
  mb: 2,
  '& .MuiOutlinedInput-root': {
    '& fieldset': {borderColor: 'primary.main'},
    '&:hover fieldset': {borderColor: 'primary.dark'},
  },
};

const HarHaBituchScreen = () => {
  const {classes} = useStyles();
  const intl = useIntl();
  const {setIdIssuanceDateProvided, setScreen, token, licensePlate} = useDocumentCollection();
  const [idIssuanceDate, setIdIssuanceDate] = useState('');
  const [dateOfBirth, setDateOfBirth] = useState('');
  const [idNumber, setIdNumber] = useState('');
  const [disclaimerApproved, setDisclaimerApproved] = useState(false);
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [showHelp, setShowHelp] = useState(false);

  useEffect(() => {
    if (submitted) {
      const timer = setTimeout(() => {
        setScreen(SCREENS.UPLOAD);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [submitted, setScreen]);

  const handleDateChange = (setter, field) => (e) => {
    const value = formatDateInput(e.target.value);
    setter(value);
    if (errors[field]) setErrors((prev) => ({...prev, [field]: ''}));
  };

  const handleIdNumberChange = (e) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 9);
    setIdNumber(value);
    if (errors.idNumber) setErrors((prev) => ({...prev, idNumber: ''}));
  };

  const handleSubmit = async () => {
    const newErrors = {};

    if (!validateDate(idIssuanceDate)) {
      newErrors.idIssuanceDate = intl.formatMessage({id: 'docCollection.harHabituch.error'});
    } else if (!validateDateNotInFuture(idIssuanceDate)) {
      newErrors.idIssuanceDate = intl.formatMessage({id: 'docCollection.harHabituch.futureDateError'});
    }
    if (!validateDate(dateOfBirth)) {
      newErrors.dateOfBirth = intl.formatMessage({id: 'docCollection.harHabituch.dateOfBirthError'});
    } else if (!validateDateNotInFuture(dateOfBirth)) {
      newErrors.dateOfBirth = intl.formatMessage({id: 'docCollection.harHabituch.futureDateError'});
    }
    if (!validateIdNumber(idNumber)) {
      newErrors.idNumber = intl.formatMessage({id: 'docCollection.harHabituch.idNumberError'});
    }
    if (!disclaimerApproved) {
      newErrors.disclaimer = intl.formatMessage({id: 'docCollection.harHabituch.disclaimerRequired'});
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    try {
      await submitHarHabituchDetails(token, {
        idIssuanceDate,
        dateOfBirth,
        idNumber,
        carLicenseNumber: licensePlate,
      });
      setSubmitted(true);
      setIdIssuanceDateProvided(true);
    } catch (err) {
      setErrors({general: intl.formatMessage({id: 'docCollection.harHabituch.submitError'})});
    }
  };

  if (submitted) {
    return (
      <div>
        <ProgressStepper activeStep={0} />
        <Box display='flex' flexDirection='column' alignItems='center' justifyContent='center' p='40px 16px' textAlign='center'>
          <CheckCircleOutlineIcon sx={{color: 'success.main', fontSize: 56, mb: 2}} />
          <Typography variant='h6' sx={{fontWeight: 700, color: 'success.main', mb: 1}}>
            <FormattedMessage id='docCollection.harHabituch.successTitle' />
          </Typography>
          <Typography variant='body2' color='text.secondary' sx={{lineHeight: 1.6}}>
            <FormattedMessage id='docCollection.harHabituch.successDesc' values={{br: <br />}} />
          </Typography>
        </Box>
      </div>
    );
  }

  return (
    <div>
      <ProgressStepper activeStep={0} />
      <div className={classes.container}>
        <Typography variant='h6' align='center' sx={{fontWeight: 700, mb: 1.5}}>
          <FormattedMessage id='docCollection.harHabituch.title' />
        </Typography>
        <Typography variant='body2' align='center' color='text.secondary' sx={{lineHeight: 1.7, mb: 3}}>
          <FormattedMessage id='docCollection.harHabituch.description' />
        </Typography>

        <button type='button' className={classes.helpLink} onClick={() => setShowHelp(true)}>
          <FormattedMessage id='docCollection.harHabituch.helpLink' />
        </button>

        {showHelp && <IdCardHelpIllustration onClose={() => setShowHelp(false)} />}

        <TextField
          fullWidth
          variant='outlined'
          className={classes.outlinedField}
          label={intl.formatMessage({id: 'docCollection.harHabituch.dateOfBirthPlaceholder'})}
          value={dateOfBirth}
          onChange={handleDateChange(setDateOfBirth, 'dateOfBirth')}
          inputProps={{inputMode: 'numeric', maxLength: 10, dir: 'rtl', style: {fontSize: 17, padding: '14px 16px'}}}
          InputLabelProps={{style: {direction: 'rtl', transformOrigin: 'top right', left: 'auto'}}}
          error={!!errors.dateOfBirth}
          helperText={errors.dateOfBirth}
          sx={outlinedFieldSx}
        />

        <TextField
          fullWidth
          variant='outlined'
          className={classes.outlinedField}
          label={intl.formatMessage({id: 'docCollection.harHabituch.idNumberPlaceholder'})}
          value={idNumber}
          onChange={handleIdNumberChange}
          inputProps={{inputMode: 'numeric', maxLength: 9, dir: 'rtl', style: {fontSize: 17, padding: '14px 16px'}}}
          InputLabelProps={{style: {direction: 'rtl', transformOrigin: 'top right', left: 'auto'}}}
          error={!!errors.idNumber}
          helperText={errors.idNumber}
          sx={outlinedFieldSx}
        />

        <TextField
          fullWidth
          variant='outlined'
          className={classes.outlinedField}
          label={intl.formatMessage({id: 'docCollection.harHabituch.placeholder'})}
          value={idIssuanceDate}
          onChange={handleDateChange(setIdIssuanceDate, 'idIssuanceDate')}
          inputProps={{inputMode: 'numeric', maxLength: 10, dir: 'rtl', style: {fontSize: 17, padding: '14px 16px'}}}
          InputLabelProps={{style: {direction: 'rtl', transformOrigin: 'top right', left: 'auto'}}}
          error={!!errors.idIssuanceDate}
          helperText={errors.idIssuanceDate}
          sx={outlinedFieldSx}
        />

        <FormControlLabel
          sx={{alignItems: 'center', mb: 2, mr: 0}}
          control={
            <Checkbox
              checked={disclaimerApproved}
              onChange={(e) => {
                setDisclaimerApproved(e.target.checked);
                if (errors.disclaimer) setErrors((prev) => ({...prev, disclaimer: ''}));
              }}
            />
          }
          label={
            <span className={classes.disclaimerText}>
              <FormattedMessage id='docCollection.harHabituch.disclaimer' />
            </span>
          }
        />
        {errors.disclaimer && (
          <Typography variant='caption' color='error' sx={{display: 'block', mb: 1, mt: -1}}>
            {errors.disclaimer}
          </Typography>
        )}

        {errors.general && (
          <Alert severity='error' sx={{mb: 2}}>{errors.general}</Alert>
        )}

        <Button
          fullWidth
          variant='contained'
          onClick={handleSubmit}
          sx={{borderRadius: 30, py: 1.5, fontSize: 18, fontWeight: 700, maxWidth: 400, mx: 'auto', display: 'block'}}
        >
          <FormattedMessage id='docCollection.harHabituch.submit' />
        </Button>

        <Button
          fullWidth
          onClick={() => setScreen(SCREENS.UPLOAD)}
          sx={{mt: 1.5, maxWidth: 400, mx: 'auto', display: 'block', textDecoration: 'underline', color: 'text.secondary'}}
        >
          <FormattedMessage id='docCollection.harHabituch.back' />
        </Button>
      </div>
    </div>
  );
};

export default HarHaBituchScreen;
